'use client'

import { motion, useInView } from 'framer-motion'
import { useRef, useState } from 'react'
import type { DisplayProject } from '@/lib/portfolioDisplay'

export function PortfolioGridCard({
  study,
  index,
}: {
  study: DisplayProject
  index: number
}) {
  const ref = useRef<HTMLAnchorElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-40px' })
  const [isHovered, setIsHovered] = useState(false)
  const href = study.caseStudyPath || study.link
  const external = !study.caseStudyPath

  return (
    <motion.a
      ref={ref}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      className="group block"
      aria-label={
        study.caseStudyPath
          ? `${study.title} - read case study`
          : `${study.title} - view live site`
      }
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.32), ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative aspect-video overflow-hidden border border-border bg-muted/30">
        <img
          src={study.image}
          alt={`${study.title} - project screenshot`}
          className="h-full w-full object-contain object-top"
          loading="lazy"
        />

        <motion.div
          className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md backdrop-blur-sm"
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.25 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M7 17L17 7M17 7H7M17 7V17" />
          </svg>
        </motion.div>
      </div>

      <div className="mt-3">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-accent">
            {study.lab ? 'Lab' : 'Live'}
          </span>
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{study.year}</span>
        </div>
        <h3 className="font-serif text-lg leading-tight text-foreground group-hover:text-accent transition-colors md:text-xl">
          {study.title}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm leading-snug text-muted-foreground">{study.subtitle}</p>
      </div>
    </motion.a>
  )
}
